import React, { useRef, useEffect } from 'react';

const CATEGORY_COLORS = {
  Jour:       'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200',
  Mois:       'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  MoisAn:     'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
  Année:      'bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200',
  Date:       'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
  Semaine:    'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  Trimestre:  'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
};

const CATEGORY_LABEL = {
  Jour:      'JOUR',
  Mois:      'MOIS',
  MoisAn:    'MOIS+AN',
  Année:     'ANNÉE',
  Date:      'DATE',
  Semaine:   'SEM.',
  Trimestre: 'TRIM.',
};

/**
 * Dropdown pour les commandes de dates dynamiques (déclenchées par "//").
 *
 * Props :
 *  - open          : booléen
 *  - items         : [{kind:'date', command, value, description, category, icon}]
 *  - onSelect      : (item) => void  — insère la valeur
 *  - anchorRect    : DOMRect | null  — position de l'éditeur
 *  - activeIndex   : number
 *  - onActiveIndexChange : (n) => void
 */
const DateDropdown = ({ open, items, onSelect, anchorRect, activeIndex, onActiveIndexChange }) => {
  const activeItemRef = useRef(null);
  const listRef = useRef(null);
  const dropdownRef = useRef(null);

  // Auto-scroll vers l'item actif
  useEffect(() => {
    if (activeItemRef.current && listRef.current) {
      const itemTop = activeItemRef.current.offsetTop;
      const itemBottom = itemTop + activeItemRef.current.offsetHeight;
      const listTop = listRef.current.scrollTop;
      const listBottom = listTop + listRef.current.offsetHeight;
      if (itemTop < listTop) listRef.current.scrollTop = itemTop;
      else if (itemBottom > listBottom) listRef.current.scrollTop = itemBottom - listRef.current.offsetHeight;
    }
  }, [activeIndex]);

  // Gestion clavier
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      if (!['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(e.key)) return;
      e.preventDefault();
      e.stopPropagation();

      if (e.key === 'ArrowDown') {
        onActiveIndexChange && onActiveIndexChange((activeIndex + 1) % items.length);
      } else if (e.key === 'ArrowUp') {
        onActiveIndexChange && onActiveIndexChange((activeIndex - 1 + items.length) % items.length);
      } else if (e.key === 'Enter') {
        if (items[activeIndex]) onSelect(items[activeIndex]);
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [open, activeIndex, items, onSelect, onActiveIndexChange]);

  // Focus automatique
  useEffect(() => {
    if (open && dropdownRef.current) dropdownRef.current.focus();
  }, [open]);

  if (!open || !items || items.length === 0) return null;

  const style = anchorRect
    ? (() => {
        const minW = 300;
        const maxW = 560;
        const width = Math.min(Math.max(minW, anchorRect.width), maxW);
        const margin = 8;
        const dropdownHeight = Math.min(380, items.length * 56 + 90);
        const spaceBelow = window.innerHeight - anchorRect.bottom;
        const spaceAbove = anchorRect.top;
        const showAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
        const top = showAbove
          ? Math.max(margin, anchorRect.top - dropdownHeight - 6)
          : Math.min(window.innerHeight - margin - dropdownHeight, anchorRect.bottom + 6);
        let left = anchorRect.left;
        left = Math.max(margin, Math.min(left, window.innerWidth - margin - width));
        return { position: 'fixed', left, top, width, zIndex: 9999 };
      })()
    : { position: 'fixed', right: 16, bottom: 80, zIndex: 9999 };

  return (
    <div
      ref={dropdownRef}
      style={style}
      className="bg-white dark:bg-gray-800 border-2 border-accent dark:border-accent rounded-lg shadow-2xl text-sm animate-fadeIn flex flex-col outline-none"
      tabIndex={-1}
      role="dialog"
      aria-label="Insertion de date dynamique"
    >
      {/* Header */}
      <div className="flex-shrink-0 px-4 py-2.5 border-b dark:border-gray-700 flex items-center gap-2 bg-accent/5 dark:bg-accent/10 rounded-t-lg">
        <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
          Date dynamique
        </span>
        <span className="ml-auto text-xs text-gray-400 dark:text-gray-500 font-mono">
          {'//'}commande
        </span>
      </div>

      {/* List */}
      <ul
        ref={listRef}
        className="overflow-y-auto flex-1"
        style={{ maxHeight: '260px' }}
        role="listbox"
      >
        {items.map((item, i) => {
          const colorClass = CATEGORY_COLORS[item.category] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
          const catLabel = CATEGORY_LABEL[item.category] || item.category.toUpperCase();

          return (
            <li
              key={`${item.command}-${i}`}
              ref={i === activeIndex ? activeItemRef : null}
              className={`px-4 py-2.5 cursor-pointer transition-colors ${
                activeIndex === i
                  ? 'bg-accent/10 dark:bg-accent/20 border-l-4 border-accent'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-700 border-l-4 border-transparent'
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(item);
              }}
              onMouseEnter={() => onActiveIndexChange && onActiveIndexChange(i)}
              role="option"
              aria-selected={activeIndex === i}
            >
              <div className="flex items-center gap-3">
                {/* Badge catégorie */}
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded flex-shrink-0 ${colorClass}`}>
                  {catLabel}
                </span>
                {/* Commande + valeur */}
                <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
                  <div>
                    <span className="font-mono text-xs font-semibold text-gray-500 dark:text-gray-400">
                      {'//​'}{item.command}
                    </span>
                    <span className="ml-2 text-sm font-semibold text-black dark:text-white">
                      → {item.value}
                    </span>
                    <span className="ml-1.5 text-xs text-accent" title="Valeur recalculée automatiquement à chaque lecture">
                      ↻
                    </span>
                  </div>
                  <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                    {item.description}
                  </span>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {/* Footer */}
      <div className="px-3 py-2 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 flex-shrink-0 rounded-b-md">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          ↑↓ naviguer • ⏎ insérer • Esc fermer &nbsp;·&nbsp; <span className="text-accent/70">↻</span> valeur recalculée à chaque lecture
        </p>
      </div>
    </div>
  );
};

export default DateDropdown;
